import { formatAddress, ValidAddress } from "@inrev/common";
import { ContactAddressField, ContactNameFields } from "@portal/components/features/contact-forms";
import { FormField, FormFieldGroup, FormItemLabel, FormRow } from "@portal/components/form";
import { FormInput, FormSSN, FormYesNo } from "@portal/components/form/fields";
import { Button } from "@portal/components/primitives";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { UseAddressValidationReturn } from "../../../../../api";
import {
	AdminIndividualOwnerFormData,
	AdminIndividualSpouseFormData,
	DefaultAdminIndividualFormData,
} from ".";

type AdminIndividualSpouseFormProps = {
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	form: UseFormReturn<DefaultAdminIndividualFormData, any, AdminIndividualSpouseFormData>;
	addressValidation: UseAddressValidationReturn;
	validatedOwnerData: AdminIndividualOwnerFormData | undefined;
	onBack: () => void;
	onSubmit: (data: AdminIndividualSpouseFormData) => void;
	loading: boolean;
};

export const AdminIndividualSpouseForm = ({
	form,
	addressValidation,
	validatedOwnerData,
	onBack,
	onSubmit,
	loading,
}: AdminIndividualSpouseFormProps) => {
	const includeSpouseInUnderwriting = form.watch("includeInUnderwriting");

	const handleSubmit = (formData: AdminIndividualSpouseFormData) => {
		onSubmit(formData);
	};

	const setAddressValueFromExistingData = (
		existingAddress: ValidAddress | undefined,
		addressValidation: UseAddressValidationReturn,
	) => {
		if (existingAddress) {
			addressValidation.setAddressValue(formatAddress(existingAddress));
		}
	};

	useEffect(() => {
		if (
			validatedOwnerData?.address &&
			includeSpouseInUnderwriting === "" &&
			addressValidation.addressFieldControl.value === ""
		) {
			setAddressValueFromExistingData(validatedOwnerData.address, addressValidation);
		}
	}, []);

	return (
		<form className="w-full h-fit flex flex-col space-y-[25px]">
			<FormRow className="justify-start gap-x-[40px]">
				<FormField className="max-w-fit">
					<FormItemLabel condensed required>
						Include spouse in underwriting?
					</FormItemLabel>
					<FormYesNo name="includeInUnderwriting" control={form.control} compact />
				</FormField>
				<FormField className="max-w-fit">
					<FormItemLabel condensed required>
						Include spouse in indemnity?
					</FormItemLabel>
					<FormYesNo name="includeInIndemnity" control={form.control} compact />
				</FormField>
			</FormRow>
			<FormFieldGroup condensed>
				<ContactNameFields control={form.control} compact />
				<ContactAddressField
					formMethods={form}
					addressControl={addressValidation.addressFieldControl}
					compact
				/>
				<FormRow>
					<FormField condensed>
						<FormItemLabel condensed>
							Email
							<span className="text-gray-400 text-[13px] ml-[8px] font-normal">If Available</span>
						</FormItemLabel>
						<FormInput name="email" control={form.control} compact />
					</FormField>
					{includeSpouseInUnderwriting && (
						<FormField className="max-w-[200px]" condensed>
							<FormItemLabel condensed>SSN</FormItemLabel>
							<FormSSN name="ssn" control={form.control} compact />
						</FormField>
					)}
				</FormRow>
			</FormFieldGroup>
			<div className="w-full flex justify-center pt-[5px] space-x-[10px]">
				<Button onClick={onBack} color="secondary" variant="outline" className="w-[150px] h-[36px]">
					Back
				</Button>
				<Button
					onClick={form.handleSubmit(handleSubmit)}
					color="primary"
					className="w-[150px] h-[36px]"
					loading={form.formState.isSubmitting || loading}
				>
					Submit
				</Button>
			</div>
		</form>
	);
};
